import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface EmailVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

export const EmailVerificationDialog = ({ 
  isOpen, 
  onClose, 
  email 
}: EmailVerificationDialogProps) => {
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });
      
      if (error) {
        // Check for rate limit errors
        const errorMessage = error.message?.toLowerCase() || '';
        if (errorMessage.includes('rate limit') || errorMessage.includes('email rate limit')) {
          toast.error("We can't send a verification email at the moment. Please try again later.");
        } else {
          throw error;
        }
        return;
      }
      
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      const errorMessage = error.message?.toLowerCase() || '';
      if (errorMessage.includes('rate limit') || errorMessage.includes('email rate limit')) {
        toast.error("We can't send a verification email at the moment. Please try again later.");
      } else {
        toast.error(error.message || 'Failed to resend verification email');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Verification Required
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              Your email address <strong>{email}</strong> has not been verified yet.
            </p>
            <p>
              Please check your inbox for the verification email we sent when you signed up.
              If you can't find it, check your spam folder or request a new one.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Resend Verification Email
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
